import React, { useState, useEffect, useCallback } from "react"
import { Mail, RefreshCw, Trash2, Clock, CheckCircle2, XCircle, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Skeleton } from "src/components/ui/skeleton"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { useToast } from "src/components/ui/toast"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "src/components/ui/alert-dialog"
import { useAuth } from "src/contexts/AuthContext"
import { NoAccess } from "src/components/shared/NoAccess"
import { fetchInvitations, deleteInvitation, resendInvitation } from "src/features/members/api/invitationsApi"

function StatusBadge({ status, expired }) {
  if (expired) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[12px] font-medium text-slate-600">
        <Clock className="h-3 w-3" />
        Expired
      </span>
    )
  }

  const config = {
    pending: {
      icon: Clock,
      label: "Pending",
      className: "bg-amber-100 text-amber-700",
    },
    accepted: {
      icon: CheckCircle2,
      label: "Accepted",
      className: "bg-emerald-100 text-emerald-700",
    },
    cancelled: {
      icon: XCircle,
      label: "Cancelled",
      className: "bg-slate-100 text-slate-600",
    },
  }

  const { icon: Icon, label, className } = config[status] || config.pending

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-medium ${className}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  )
}

function SkeletonRow() {
  return (
    <TableRow>
      {[40, 220, 180, 160, 120, 100].map((w, i) => (
        <TableCell key={i}>
          <Skeleton className="h-4 rounded" style={{ width: w }} />
        </TableCell>
      ))}
    </TableRow>
  )
}

function EmptyState() {
  return (
    <TableRow>
      <TableCell colSpan={6}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Mail className="mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">No invitations sent yet</p>
          <p className="mt-1 text-xs text-slate-400">
            Use the "Invite Member" button on the Members page to send invitations
          </p>
        </div>
      </TableCell>
    </TableRow>
  )
}

export default function InvitationsPage() {
  const { toast } = useToast()
  const { can } = useAuth()
  const canRead = can("memberships", "read")

  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionTarget, setActionTarget] = useState(null)
  const [actionType, setActionType] = useState(null) // 'delete' | 'resend'
  const [actionLoading, setActionLoading] = useState(false)

  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState(null)

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") setSortDirection("desc")
      else if (sortDirection === "desc") {
        setSortField(null)
        setSortDirection(null)
      }
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const loadInvitations = useCallback(async () => {
    setLoading(true)
    try {
      const { invitations: data } = await fetchInvitations()
      setInvitations(data || [])
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load invitations",
      })
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    if (canRead) loadInvitations()
  }, [loadInvitations, canRead])

  if (!canRead) {
    return <NoAccess message="You don't have permission to view invitations for this organization." />
  }

  const sortedInvitations = React.useMemo(() => {
    if (!sortField || !sortDirection) return invitations
    return [...invitations].sort((a, b) => {
      let aVal, bVal
      if (sortField === "email") {
        aVal = a.email || ""
        bVal = b.email || ""
      } else if (sortField === "role") {
        aVal = a.role?.name || ""
        bVal = b.role?.name || ""
      } else if (sortField === "invited_by") {
        aVal = a.from_user?.name || ""
        bVal = b.from_user?.name || ""
      } else if (sortField === "status") {
        aVal = a.status || ""
        bVal = b.status || ""
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else {
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1
        return 0
      }
    })
  }, [invitations, sortField, sortDirection])

  const renderSortableHeader = (label, field, className = "") => {
    const isSorted = sortField === field
    return (
      <button
        onClick={() => handleSort(field)}
        className={`inline-flex items-center gap-1 hover:text-black focus:outline-none font-semibold text-black ${className}`}
      >
        <span>{label}</span>
        {isSorted ? (
          sortDirection === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
        )}
      </button>
    )
  }

  async function handleDelete() {
    if (!actionTarget) return

    setActionLoading(true)
    try {
      await deleteInvitation(actionTarget.id)
      toast({ title: "Invitation cancelled" })
      setActionTarget(null)
      setActionType(null)
      loadInvitations()
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to cancel invitation",
      })
    } finally {
      setActionLoading(false)
    }
  }

  async function handleResend() {
    if (!actionTarget) return

    setActionLoading(true)
    try {
      await resendInvitation(actionTarget.id)
      toast({
        title: "Invitation resent",
        description: `A new invitation email has been sent to ${actionTarget.email}`,
      })
      setActionTarget(null)
      setActionType(null)
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.errors?.[0] || "Failed to resend invitation",
      })
    } finally {
      setActionLoading(false)
    }
  }

  function formatDate(isoString) {
    const date = new Date(isoString)
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date)
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900">Invitations</h2>
        <p className="text-[13px] text-slate-500">
          {invitations.length > 0
            ? `${invitations.length} invitation${invitations.length !== 1 ? "s" : ""}`
            : "Manage pending and past invitations"}
        </p>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-[50px]">#</TableHead>
              <TableHead className="min-w-[240px] text-black">{renderSortableHeader("Email", "email")}</TableHead>
              <TableHead className="w-[180px] text-black">{renderSortableHeader("Role", "role")}</TableHead>
              <TableHead className="w-[160px] text-black">{renderSortableHeader("Invited By", "invited_by")}</TableHead>
              <TableHead className="w-[140px] text-black">{renderSortableHeader("Status", "status")}</TableHead>
              <TableHead className="w-[100px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : invitations.length === 0 ? (
              <EmptyState />
            ) : (
              sortedInvitations.map((invitation, idx) => {
                const canResend = invitation.status === "pending" && !invitation.expired
                const canDelete = invitation.status === "pending"

                return (
                  <TableRow key={invitation.id}>
                    <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <span className="text-[13px] font-medium text-slate-900">
                          {invitation.email}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell className="text-[13px] text-slate-600">
                      {invitation.role?.name}
                    </TableCell>

                    <TableCell className="text-[13px] text-slate-600">
                      {invitation.from_user?.name}
                    </TableCell>

                    <TableCell>
                      <StatusBadge status={invitation.status} expired={invitation.expired} />
                    </TableCell>

                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {canResend && (
                          <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setActionTarget(invitation)
                                setActionType("resend")
                              }}
                              title="Resend invitation"
                          >
                            <RefreshCw className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setActionTarget(invitation)
                                setActionType("delete")
                              }}
                              title="Cancel invitation"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-rose-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={actionType === "delete"} onOpenChange={(open) => !open && setActionTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the invitation for <strong>{actionTarget?.email}</strong>?
              They will no longer be able to accept this invitation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={actionLoading}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {actionLoading ? "Cancelling..." : "Yes, Cancel Invitation"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resend Confirmation Dialog */}
      <AlertDialog open={actionType === "resend"} onOpenChange={(open) => !open && setActionTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resend Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Send another invitation email to <strong>{actionTarget?.email}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResend} disabled={actionLoading}>
              {actionLoading ? "Sending..." : "Yes, Resend"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
